import chartTypes from '../supportedChartTypes';
import { mergeDeep, replaceMultipleStrings, isEdge, isIE } from '../util';

class FC_Accessibility_Base {
    constructor(svgRoot, chartInstance, alternateTexts, config) {
        //Root SVG element
        this.svgRoot = svgRoot;

        //Instance of FusionCharts object
        this.chartInstance = chartInstance;

        //The alternate texts to be read out by screen reader
        this.alternateTexts = alternateTexts;

        //Additional configuration for accessibility
        this.config = config;

        //Chart JSON data
        this.chartData = chartInstance.getJSONData();

        //Type of the chart
        this.chartType = this.chartInstance.chartType();

        //If browser is IE
        this.isIE = isIE();

        //Key value pairs, the keys are the macros used in alternate texts and can be set by users, while the values are picked dynamically from the chart which will be replaced in place of the macros
        this.chartPropertyMap = {
            "{chartType}": (this.config && this.config.chartType) || this._getFormattedChartName(this.chartType),
            "{caption}": this.chartData.chart.caption,
            "{subcaption}": this.chartData.chart.subcaption
        }
    }

    //Get display name from chart type
    _getFormattedChartName(chartType) {
        return chartTypes.hasOwnProperty(chartType) ? chartTypes[chartType].displayName : chartType;
    }

    /**
     * Add axis related information in chart property map
     */
    addAxisInformation() {
        this.chartPropertyMap = mergeDeep(this.chartPropertyMap, {
            "{xAxisName}": this.chartData.chart.xaxisname || '',
            "{yAxisName}": this.chartData.chart.yaxisname || ''
        });
    }

    /**
     * Add tab index and aria attribute to a SVG element
     * @param {*} elem SVG element on which tabindex and aria attributes will be applied
     * @param {*} txtToReplace Text to replace in string
     * @param {*} propertyMap Key value pair which will be used to replace macros inside a text with chart specific values
     * @param {*} tabindex Tabindex to set on the element - usually 0 or 1
     * @param {*} role Aria role to add on the element
     */
    addTabIndexAndSetAria(elem, txtToReplace, propertyMap, tabindex, role) {
        if(elem) {
            let tabIndexText = 'tabindex';
            if(isEdge()) {
                //Edge browser seems to have some problem if the attribute name is tabindes
                //Edge works well with tabIndex but all other browser stops working with tabIndex
                tabIndexText = 'tabIndex';
            }
            tabindex = tabindex || "0";
            elem.setAttribute(tabIndexText, tabindex || "0");

            // if(tabindex == "0") {
            //     elem.setAttribute('focusable', 'true');
            // }

            //For IE make all elements navigable by TAB key only, as keyboard navigation does not work for IE
            //Although focusable attribute is applied to all elements but as of now only IE works on this attribute
            //All other browser ignore the attribute as of now, hence a redundant check is ommitted to check the browser
            //If browser behavior changes with the release of SVG2 or any browser changing their own behavior, we need to update this section

            //Performing this test and enabling only for IE will make Edge browser work with Narrator flawlessly
            //But not work with JAWS
            //Hence a middle ground is followed - IE and Edge behavior is made similar by removing the check
            //if(this.isIE) {
                elem.setAttribute('focusable', 'true');
            //}

            // if(!(this.config.readerPreferenceForEdge == 'narrator' && !this.isIE)) {
            //    elem.setAttribute('focusable', 'true');
            // }

            txtToReplace && propertyMap && elem.setAttribute('aria-label', replaceMultipleStrings(txtToReplace, propertyMap));
            role && elem.setAttribute('role', role);
        }
    }

    /**
     * Make a chart accessible
     * @param {*} plotEventHandler The event handler which will add all the necessary aria of all elements
     */
    makeChartAccessible(plotEventHandler) {

        //TO-DO : Evaluate if all plot information should be included in chart aria
        //And generalize below section for all charts
        if(this.chartType == "funnel") {
            let label, value;
            for(let i=0; i<this.chartData.data.length; i++) {
                if(this.chartData.data[i].value) {
                    label = this.chartData.data[i].label;
                    value = this.chartInstance.formatNumber(this.chartData.data[i].value);
                    break;
                }
            }
            this.chartPropertyMap = mergeDeep(this.chartPropertyMap, {
                '{plot-0-label}': label || '',
                '{plot-0-value}': value || ''
            });
        }

        //SVG root element is marked as role="application", this will enable JAWS and other screen readers to behave properly for keyboard navigation
        this.addTabIndexAndSetAria(this.svgRoot, this.alternateTexts.chart, this.chartPropertyMap, "0", "application");

        let backButton = this.chartInstance.jsVars.overlayButton;
        if (backButton) {
          function backButtonKeyPress(event) {
            event.stopPropagation();
            switch (event.keyCode) {
              case 13:
                //Enter
              case 32:
                //spacebar
                backButton.click();
                break;
            }
          }
          this.addTabIndexAndSetAria(backButton, "Back Button. Click this button to go back to the previous chart.", this.chartPropertyMap, "0", "button");
          backButton.addEventListener('keydown', backButtonKeyPress.bind(this), false);
        }

        if(plotEventHandler) {
            this.svgRoot.addEventListener('focus', plotEventHandler.bind(this), {once: true});
        }
    }
}

export default FC_Accessibility_Base;